import { useNavigate } from "react-router-dom";
import {
  getStep,
  nextStep,
  planSubFlows,
  ONBOARDING_BASE_PATH,
  SUB_FLOW_STEP_ID,
} from "../../registry";
import { useFlowState, useFlowActions } from "../../../state/FlowProvider";
import { normalizeSectorSelection } from "../../../components/Sectors/sectorSelection";

/**
 * Everything a goal sub-flow screen shares with the other four.
 *
 * The screens themselves are ordinary screens — each owns its form state, its
 * heading and its Continue, exactly as `GoalsStep` does. What none of them can
 * own alone is its place in the sequence: which questions are still its to ask
 * once the other sub-flows have deduped against it, where Continue goes, and
 * how far the global bar has moved. That is what this hook supplies, over
 * `planSubFlows` — pure, cross-aware, and tested on its own rather than through
 * five rendered screens.
 *
 * @param {string} goal
 * @returns {{asks: object, index: number, total: number, answers: object,
 *   submit: (answers?: object) => void}}
 */

const SUB_FLOW_BASE = `${ONBOARDING_BASE_PATH}/${getStep(SUB_FLOW_STEP_ID).path}`;

export const useGoalSubflow = (goal) => {
  const state = useFlowState();
  const { mergeGoalData } = useFlowActions();
  const navigate = useNavigate();

  const plan = planSubFlows(state.goals);
  const index = plan.findIndex((s) => s.goal === goal);

  // Named for what it does: persist this sub-flow's answers, then move on.
  // "next" alone would describe only the navigation, and the write is the half
  // that makes the following sub-flow reachable.
  const submit = (answers = {}) => {
    // Records the sub-flow as completed, which is what makes the next one
    // reachable, and stores whatever it collected. REPLACES this goal's object
    // rather than merging into it: the screen owns its whole answer set, so
    // revisiting and deselecting has to be able to remove something.
    //
    // The entry is what marks the sub-flow done, whatever it holds — the guard
    // checks presence, not content.
    mergeGoalData({ [goal]: answers });

    const next = plan[index + 1];
    if (next) {
      navigate(`${SUB_FLOW_BASE}/${next.param}`);
      return;
    }

    // Routed against the state this answer produces, not the one on screen.
    // The dispatch has not landed yet, and `profile` is unreachable until every
    // sub-flow has an entry — so asking the registry about the old state would
    // strand the member on the last sub-flow with nowhere to continue to.
    // `GoalsStep` routes against its pending selection for the same reason.
    const after = nextStep(SUB_FLOW_STEP_ID, {
      ...state,
      goalData: { ...state.goalData, [goal]: answers },
    });
    if (after) navigate(`${ONBOARDING_BASE_PATH}/${after.path}`);
  };

  const answers = state.goalData?.[goal] ?? {};
  const normalizedAnswers = answers.sectors
    ? { ...answers, sectors: normalizeSectorSelection(answers.sectors) }
    : answers;

  return {
    asks: plan[index]?.asks ?? {},
    index,
    total: plan.length,
    answers: normalizedAnswers,
    submit,
  };
};
